import type { AgentSessionApiId } from '@/model/Agent.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useMemo, type FC, type PropsWithChildren } from 'react';
import { fdaRuleTextsOf, SendFdaRuleTextsContext } from '@/components/Agents/SendValidator/utils/sendFdaRuleTexts';

interface SendFdaRuleTextsProviderProps {
  sessionApiId: AgentSessionApiId;
}

/**
 * Fetches, once per report, the FDA's text for the validator rules the session's result cites, and
 * only those: the server returns no rule the result does not cite. Until it arrives, or if it fails,
 * the ids still show, without their text.
 */
const SendFdaRuleTextsProvider: FC<PropsWithChildren<SendFdaRuleTextsProviderProps>> = ({ sessionApiId, children }) => {
  const { response } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/send-validator/{sessionApiId}/fda-validator-rules',
    path: { sessionApiId },
    options: { onError: { toast: false, throw: false, capture: true, slug: 'send-validator-fda-rule-texts-load' } },
  });
  const value = useMemo(
    () => (response?.type === 'success' ? fdaRuleTextsOf(response.body) : { texts: new Map() }),
    [response]
  );

  return <SendFdaRuleTextsContext value={value}>{children}</SendFdaRuleTextsContext>;
};

export default SendFdaRuleTextsProvider;
